import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { dimensionLabel } from "../lib/scoring";

// Mode-aware section labels. ORI: Tier 1 is required + team-framed,
// Tier 3 is "facilitator-meta". PRI: every tier is optional and the
// language is individual/coach-framed. Picked from data.deployment_mode
// at render time so the headers can't contradict the prompt text.
const TIER_LABELS_BY_MODE = {
  organization: {
    1: "Tier 1 — Required (team)",
    2: "Tier 2 — Optional (per dimension)",
    3: "Tier 3 — Optional (facilitator-meta)",
  },
  individual: {
    1: "Tier 1 — Optional (you)",
    2: "Tier 2 — Optional (per dimension)",
    3: "Tier 3 — Optional (coach-meta)",
  },
};

function tierLabelsFor(mode) {
  return TIER_LABELS_BY_MODE[mode === "individual" ? "individual" : "organization"];
}

export default function Reflections({ deploymentId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openTiers, setOpenTiers] = useState({ 1: true, 2: false, 3: false });
  const [revealDemo, setRevealDemo] = useState(false);
  const [tierFilter, setTierFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/deployments/${deploymentId}/text-responses`, {
          params: revealDemo ? { reveal_demographics: true } : {},
        });
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't load reflections.");
      }
    })();
  }, [deploymentId, revealDemo]);

  if (error) return <div className="panel text-sm" style={{ borderColor: "#AD393E" }}>{error}</div>;
  if (!data) return <div className="text-xs text-gray-500">Loading…</div>;

  const visiblePrompts =
    tierFilter === "all" ? data.prompts : data.prompts.filter((p) => p.tier === Number(tierFilter));

  const csvUrl = `${process.env.REACT_APP_BACKEND_URL}/api/deployments/${deploymentId}/text-responses.csv${revealDemo ? "?reveal_demographics=true" : ""}`;

  return (
    <div className="space-y-8" data-testid="reflections-tab">
      {/* Discipline banner — persistent, not a tooltip */}
      <DisciplineBanner note={data.discipline_note} anonymizationNote={data.anonymization_note} />

      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Reflections</div>
          <p className="text-sm text-gray-700 max-w-prose">
            Read the reflections. Form your own hypotheses. The signal is in the words and the
            moments they come up in your conversation — not in any pattern we could extract.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="text-xs px-3 py-2 border border-gray-100 bg-paper"
            style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}
            data-testid="tier-filter"
            aria-label="Filter reflections by tier"
          >
            <option value="all">All tiers</option>
            <option value="1">Tier 1 only</option>
            <option value="2">Tier 2 only</option>
            <option value="3">Tier 3 only</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={revealDemo}
              onChange={(e) => setRevealDemo(e.target.checked)}
              data-testid="reveal-demographics"
            />
            <span style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>SHOW DEMOGRAPHICS</span>
          </label>
          <a
            href={csvUrl}
            className="btn-quiet text-xs"
            style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}
            data-testid="csv-export"
          >
            EXPORT CSV ↓
          </a>
        </div>
      </div>

      {[1, 2, 3].map((tier) => {
        const promptsInTier = visiblePrompts.filter((p) => p.tier === tier);
        if (promptsInTier.length === 0) return null;
        const totalResponses = promptsInTier.reduce((s, p) => s + p.response_count, 0);
        const tierLabels = tierLabelsFor(data.deployment_mode);
        return (
          <TierSection
            key={tier}
            tier={tier}
            label={tierLabels[tier]}
            prompts={promptsInTier}
            totalResponses={totalResponses}
            open={openTiers[tier]}
            onToggle={() => setOpenTiers({ ...openTiers, [tier]: !openTiers[tier] })}
          />
        );
      })}
    </div>
  );
}

function DisciplineBanner({ note, anonymizationNote }) {
  return (
    <div
      className="border-l-4 py-5 px-6"
      style={{ borderColor: "#AD393E", background: "#FAF7F1" }}
      data-testid="discipline-banner"
    >
      <div className="eyebrow mb-3" style={{ color: "#AD393E" }}>
        Read the words. Trust the conversation.
      </div>
      <p className="text-sm leading-relaxed text-ink mb-3 max-w-prose">{note}</p>
      {anonymizationNote && (
        <p className="text-xs text-gray-700 leading-relaxed max-w-prose" data-testid="anonymization-note">
          <strong style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>ANONYMIZATION:</strong>{" "}
          {anonymizationNote}
        </p>
      )}
    </div>
  );
}

function TierSection({ tier, label, prompts, totalResponses, open, onToggle }) {
  return (
    <section className="border-t border-gray-100 pt-6" data-testid={`tier-section-${tier}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-baseline justify-between text-left mb-2 transition-colors duration-100 hover:text-red"
        data-testid={`tier-toggle-${tier}`}
      >
        <div className="flex items-baseline gap-3">
          <span style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: "#6F6F6F" }}>
            {open ? "▾" : "▸"}
          </span>
          <h3 style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A", fontSize: "1.5rem" }}>
            {label}
          </h3>
        </div>
        <div className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.15em" }}>
          {totalResponses} response{totalResponses === 1 ? "" : "s"}
        </div>
      </button>
      {open && (
        <div className="space-y-10 mt-6 pl-6">
          {prompts.map((p) => (
            <PromptResponses key={p.number} prompt={p} />
          ))}
        </div>
      )}
    </section>
  );
}

function PromptResponses({ prompt }) {
  return (
    <div data-testid={`prompt-block-${prompt.code}`}>
      <div className="flex items-baseline justify-between mb-3 gap-4">
        <div>
          <div className="eyebrow mb-2" style={{ color: prompt.required ? "#AD393E" : "#4592A8" }}>
            {prompt.code}
            {prompt.dimension ? ` · ${dimensionLabel(prompt.dimension)}` : ""}
            {prompt.required ? " · Required" : " · Optional"}
          </div>
          <p className="text-base text-ink max-w-prose" style={{ fontFamily: "Inter", fontWeight: 500, lineHeight: 1.45 }}>
            {prompt.prompt}
          </p>
        </div>
        <div className="text-xs text-gray-500 shrink-0" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
          {prompt.response_count} of n
        </div>
      </div>

      {prompt.response_count === 0 ? (
        <div className="py-4 px-5 bg-cream text-sm text-gray-500" data-testid={`empty-${prompt.code}`}>
          No responses to this prompt. The absence is signal too.
        </div>
      ) : (
        <div className="space-y-2">
          {prompt.responses.map((r, i) => (
            <div
              key={`${prompt.code}-${i}`}
              className="flex items-start gap-4 py-3 px-4 bg-cream border border-gray-100"
              data-testid={`response-${prompt.code}-${r.anon_id}`}
            >
              <div
                className="text-xs shrink-0 pt-0.5"
                style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: "#2F6B7C", minWidth: "44px" }}
              >
                {r.anon_id}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm text-ink leading-relaxed"
                  style={{ fontFamily: "Inter", maxWidth: "60ch", whiteSpace: "pre-wrap" }}
                >
                  {r.text}
                </p>
                {r.prompt_approximate && (
                  <div
                    className="text-xs mt-2"
                    style={{ fontFamily: "Inter", color: "#AD393E", fontStyle: "italic" }}
                    data-testid={`approximate-flag-${prompt.code}-${r.anon_id}`}
                    title="No prompt-text snapshot was recorded at submission. The prompt above is reconstructed from the current library."
                  >
                    prompt text approximate
                  </div>
                )}
                {r.demographics && Object.keys(r.demographics).length > 0 && (
                  <div className="text-xs text-gray-500 mt-2" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                    {Object.entries(r.demographics)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
